import { useState } from 'react';
import { uploadProposalFile, uploadQuotationFile } from '../documents/ProposalFiles';
import { apiPostLive } from '../../lib/api-client';
import { applyRecordAssist, assistEditable, assistRole, type AssistChange } from '../../lib/record-assist';
import { apiError } from '../../lib/utils';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { TextArea } from '../ui/Input';
import { SideBySideDiff } from '../ui/SideBySideDiff';
import { useNotice } from '../ui/Notice';

const MAX_FILES = 5;
const MAX_BYTES = 20 * 1024 * 1024;

function fileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || '');
      const at = value.indexOf(',');
      resolve(at >= 0 ? value.slice(at + 1) : value);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });
}

export function EditWithAiButton({ kind, status, onClick }: { kind: string; status: unknown; onClick: () => void }) {
  const enabled = assistEditable(kind, status);
  return <Button variant="outline" disabled={!enabled} title={enabled ? 'Edit with AI' : 'This status cannot be edited'} onClick={onClick}>Edit with AI</Button>;
}

function coverAllowed(side: 'proposal' | 'quotation' | 'both', proposalStatus?: string, quotationStatus?: string): boolean {
  const proposalOk = !proposalStatus || assistEditable('proposal', proposalStatus);
  const quotationOk = !quotationStatus || assistEditable('quotation', quotationStatus);
  if (side === 'proposal') return proposalOk;
  if (side === 'quotation') return quotationOk;
  return proposalOk && quotationOk;
}

function initialCover(proposalStatus?: string, quotationStatus?: string): 'proposal' | 'quotation' | 'both' {
  if (coverAllowed('both', proposalStatus, quotationStatus)) return 'both';
  if (coverAllowed('proposal', proposalStatus, quotationStatus)) return 'proposal';
  return 'quotation';
}

export function RecordAssistDialog({ kind, mode, recordId, proposalId, quotationId, proposalStatus, quotationStatus, current, title, instruction: initialInstruction, onClose, onManual, onSaved }: {
  kind: string;
  mode: 'create' | 'edit';
  recordId?: string;
  proposalId?: string;
  quotationId?: string;
  proposalStatus?: string;
  quotationStatus?: string;
  current?: Record<string, unknown>;
  title: string;
  instruction?: string;
  onClose: () => void;
  onManual?: () => void;
  onSaved: (saved: Record<string, unknown>) => void;
}) {
  const notice = useNotice();
  const [instruction, setInstruction] = useState(initialInstruction || '');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [changes, setChanges] = useState<AssistChange[] | null>(null);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [roleTitle, setRoleTitle] = useState(assistRole(kind));
  const [error, setError] = useState('');
  const [cover, setCover] = useState<'proposal' | 'quotation' | 'both'>(() => initialCover(proposalStatus, quotationStatus));
  const [reviewTab, setReviewTab] = useState<'proposal' | 'quotation'>('proposal');
  const selected = (changes || []).filter((change) => picked[change.key] !== false);
  const offer = kind === 'sales-offer';
  const proposalChanges = (changes || []).filter((change) => change.key.startsWith('proposal.'));
  const quotationChanges = (changes || []).filter((change) => change.key.startsWith('quotation.'));
  const tabbed = offer && cover === 'both' && changes !== null;
  const visible = tabbed ? (reviewTab === 'proposal' ? proposalChanges : quotationChanges) : (changes || []);

  async function payload() {
    return Promise.all(files.map(async (file) => ({
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      dataBase64: await fileBase64(file),
    })));
  }

  async function draft(rework: boolean) {
    setError('');
    if (rework && !comment.trim()) {
      setError('Add a comment for the rework.');
      return;
    }
    const row = await notice.run(`Drafting the ${title.toLowerCase()}`, async (log, signal) => {
      const attachments = await payload();
      return apiPostLive<{ roleTitle?: string; changes?: AssistChange[] }>('/api/record-assist', {
        kind,
        mode,
        recordId,
        instruction,
        comment: rework ? comment : undefined,
        cover: offer ? cover : undefined,
        proposalId,
        quotationId,
        current,
        attachments,
      }, log, signal);
    }, 'Draft ready', { confirm: false });
    if (!row) return;
    const next = row.changes || [];
    setRoleTitle(row.roleTitle || assistRole(kind));
    setChanges(next);
    setPicked(Object.fromEntries(next.map((change) => [change.key, true])));
    setComment('');
    if (!next.length) setError('Nothing in the instruction or the files could be placed on this record.');
  }

  async function proceed() {
    if (!selected.length) return;
    setError('');
    const saved = await notice.run('Saving', async () => applyRecordAssist(kind, recordId, selected, { proposalId, quotationId }), 'Saved', { confirm: false });
    if (!saved) return;
    const savedProposalId = String(saved.proposalId || (kind === 'proposal' ? saved.id || '' : ''));
    const savedQuotationId = String(saved.quotationId || (kind === 'quotation' ? saved.id || '' : ''));
    try {
      if (savedProposalId) {
        for (const file of files) await uploadProposalFile(savedProposalId, file);
      } else if (savedQuotationId) {
        for (const file of files) await uploadQuotationFile(savedQuotationId, file);
      }
    } catch (err) {
      notice.failure(apiError(err, 'The record was saved. The file was not stored.'));
    }
    onSaved(saved);
    onClose();
  }

  function addFiles(list: FileList | null) {
    const next = [...files];
    for (const file of Array.from(list || [])) {
      if (file.size > MAX_BYTES) {
        setError(`${file.name} is over 20 MB`);
        continue;
      }
      if (next.length >= MAX_FILES) break;
      next.push(file);
    }
    setFiles(next);
  }

  return (
    <Dialog
      title={mode === 'edit' ? `Edit ${title.toLowerCase()} with AI` : `New ${title.toLowerCase()}`}
      onClose={onClose}
      large={!changes}
      roomy={!changes}
      wide={Boolean(changes)}
      footer={changes ? (
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => void draft(true)}>Rework</Button>
          <Button disabled={!selected.length} onClick={() => void proceed()}>Proceed</Button>
        </>
      ) : (
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {onManual ? <Button variant="outline" onClick={onManual}>Enter manually</Button> : null}
          <Button onClick={() => void draft(false)}>Draft with {roleTitle}</Button>
        </>
      )}
    >
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {changes ? (
        <>
          <p className="text-sm">{roleTitle} prepared these changes. Untick any you do not want. Add a comment and choose Rework to send it back. Proceed saves the ticked changes. Cancel saves nothing.</p>
          {tabbed ? (
            <div className="flex flex-wrap gap-2">
              {([['proposal', 'Proposal', proposalChanges.length], ['quotation', 'Quotation', quotationChanges.length]] as const).map(([value, label, count]) => (
                <button key={value} type="button" className={`rounded-full border px-3 py-1 text-sm ${reviewTab === value ? 'border-[var(--bo-accent)] bg-[var(--bo-accent)] text-white' : 'border-[var(--bo-border)]'}`} onClick={() => setReviewTab(value)}>{label} ({count})</button>
              ))}
            </div>
          ) : null}
          {visible.length ? (
            <div className="space-y-3">
              {visible.map((change) => (
                <div key={change.key} className="rounded-lg border border-[var(--bo-border)] p-3 text-sm">
                  <label className="flex items-center gap-3 font-medium">
                    <input type="checkbox" checked={picked[change.key] !== false} onChange={(event) => setPicked((current) => ({ ...current, [change.key]: event.target.checked }))} />
                    {change.label}
                  </label>
                  <SideBySideDiff before={change.before || ''} after={change.after || ''} />
                </div>
              ))}
            </div>
          ) : tabbed ? <p className="text-sm text-[var(--bo-muted)]">No changes on this side.</p> : null}
          <TextArea value={comment} placeholder="Comment for a rework" onChange={(event) => setComment(event.target.value)} />
        </>
      ) : (
        <>
          <p className="text-sm">{roleTitle} will read what you write and any files, then show each change before anything is saved. A price is used only when it is written. Enter manually to use the form instead.</p>
          {offer ? (
            <div className="flex flex-wrap gap-2">
              {([['proposal', 'Proposal only'], ['quotation', 'Quotation only'], ['both', 'Both']] as const).map(([value, label]) => {
                const enabled = mode === 'create' || coverAllowed(value, proposalStatus, quotationStatus);
                return <button key={value} type="button" disabled={!enabled} className={`rounded-full border px-3 py-1 text-sm disabled:opacity-50 ${cover === value ? 'border-[var(--bo-accent)] bg-[var(--bo-accent)] text-white' : 'border-[var(--bo-border)]'}`} onClick={() => setCover(value)}>{label}</button>;
              })}
            </div>
          ) : null}
          <TextArea value={instruction} placeholder={`What should this ${title.toLowerCase()} say?`} onChange={(event) => setInstruction(event.target.value)} />
          <div className="text-sm">
            <label className="inline-flex cursor-pointer items-center rounded-lg border border-[var(--bo-border)] px-3 py-2">
              Attach a file
              <input className="hidden" type="file" multiple onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }} />
            </label>
            <ul className="mt-2 space-y-1">
              {files.map((file) => (
                <li key={file.name} className="flex items-center justify-between gap-3">
                  <span>{file.name}</span>
                  <button type="button" className="text-[var(--bo-link)]" onClick={() => setFiles((current) => current.filter((item) => item !== file))}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </Dialog>
  );
}
